import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import type {
  MapSearchArea,
  MapSearchAreaCopy,
} from '../../shared/results_page/map_search_area.js';
import type { HotelMapView, HotelOfferView } from '../models.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import {
  ResultsLayout,
  type ResultsAsideFrame,
  type ResultsLayoutProps,
} from '../../shared/results_page/results_layout.js';
import { HOTEL_NARROW_QUERY } from '../search/hotel_narrow.js';
import { useSkeletonMotion } from '../../shared/skeleton_motion.js';
import { HotelMap } from './hotel_map.js';
import type { HotelResultsCopy } from './hotel_results.js';
import { decisionNotice, ResultsNotice } from '../../shared/results_page/results_notice.js';
import {
  resultsShareOf,
  titleShareOf,
  type ResultsPdfShare,
} from '../../shared/results_page/results_share.js';
import {
  ResultsOfflineRequest,
  type ResultsOfflineRequestProps,
} from '../../shared/results_page/results_offline_request.js';
import {
  ResultsFilteredEmpty,
  type ResultsFilteredEmptyCopy,
} from '../../shared/results_page/results_filtered_empty.js';
import {
  activeFilterCount,
  HotelResultsListArea,
  type HotelResultsNotice,
} from './hotel_results_list.js';
import {
  EMPTY_HOTEL_RESULTS_FILTERS,
  HotelResultsFilters,
  matchesHotelResultsFilters,
  type HotelFilterableOffer,
  type HotelResultsFiltersCopy,
  type HotelResultsFilterValue,
} from './hotel_results_filters.js';
import { HotelResultsFiltersSheet } from './hotel_results_filters_sheet.js';
import {
  HotelFilterChipsSkeleton,
  HotelResultsFiltersSkeleton,
} from './hotel_results_filters_skeleton.js';
import {
  HotelResultsFilterChips,
  type HotelResultsChipsCopy,
} from './hotel_results_filter_chips.js';
import {
  HotelResultsSort,
  sortHotelOffers,
  type HotelResultsOrder,
  type HotelResultsSortCopy,
} from './hotel_results_sort.js';

export type HotelResultsPageCopy = {
  /** O título de escolha, já formatado pelo host — "Hotéis em {lugar} · {datas}". */
  title: string;
  /** A leitura que segue a contagem na linha da meta ("{n} noites"): o
   *  multiplicador do preço. O lugar não entra — já está no título (#5015). */
  context?: string;
  totalHotelsLabel(total: number): string;
  visibleHotelsLabel(visible: number, total: number): string;
  /** O compartilhar da busca, ao lado do título. Só aparece com `onShare`. */
  shareLabel: string;
  /** "Mapa": o botão flutuante que leva ao mapa em tela cheia quando ele não
   *  cabe ao lado (#2665, o par do `showListLabel` do mapa cheio) e, no palco
   *  estreito map-centric, o que devolve a folha cheia ao híbrido (#3106). */
  showMapLabel: string;
  /** O X da prévia do pino ancorada no pé do mapa, no palco estreito ("Fechar
   *  a prévia do hotel", #3108). */
  mapPreviewCloseLabel: string;
  /** O corpo da lista quando o corte esconde TODOS os hotéis (#3217 C4). */
  filteredEmpty: ResultsFilteredEmptyCopy;
  sort: HotelResultsSortCopy;
  /** A fila de chips de filtro/ordenação do palco estreito (#3140). */
  chips: HotelResultsChipsCopy;
  /** "Buscar nesta área"/"Buscando…" do mapa do palco estreito (#3114); só
   *  com `onSearchArea`. */
  searchArea?: MapSearchAreaCopy;
};

export type HotelResultsPageProps = {
  map: HotelMapView;
  offers: readonly HotelFilterableOffer[];
  currency: string;
  resultsCopy: HotelResultsCopy;
  filtersCopy: HotelResultsFiltersCopy;
  copy: HotelResultsPageCopy;
  /** Controlados no chamador, como no painel da lista completa: uma fonte só
   *  para esta página e para o mapa em tela cheia. */
  filters: HotelResultsFilterValue;
  order: HotelResultsOrder;
  selectedHotelId?: string;
  locked?: boolean;
  total?: number;
  hasMore?: boolean;
  loadingMore?: boolean;
  loadMoreLabel?: string;
  notice?: HotelResultsNotice;
  onQueryChange(query: { filters: HotelResultsFilterValue; order: HotelResultsOrder }): void;
  onLoadMore?: () => void;
  /** Trava só a NAVEGAÇÃO das linhas (abrir o detalhe); default: segue `locked`.
   *  Existe para a superfície read-only para a CONVERSA mas navegável. */
  detailsLocked?: boolean;
  onMapExpand(): void;
  onOpenDetails(hotelId: string): void;
  onShare?: () => void;
  /** O "Baixar PDF" do compartilhar (#5156): com ele o botão vira o menu, e o
   *  PDF recebe os hotéis na ordem e com os filtros da tela. */
  pdfShare?: ResultsPdfShare<HotelOfferView>;
  /**
   * Onde a fila de chips de filtro/ordenação monta no palco ESTREITO (#3140):
   * o slot que o host abre sob a pílula de busca, na top bar dele. Presente, a
   * linha da meta da folha fica só com a contagem — `Filtros` e o ordenador
   * vão para a fila. Ausente (o largo, um host sem slot), nada muda: os
   * controles seguem na linha da meta.
   */
  chipsContainer?: Element | null;
  /** Liga o "buscar nesta área" no mapa do palco estreito (#3114, ver
   *  `HotelMap.onSearchArea`): o host re-busca a área visível. Ausente ⇒ o
   *  botão nunca arma. */
  onSearchArea?: (area: MapSearchArea) => void | Promise<void>;
  /** Coleta inicial em andamento: enquanto TRUE o gesto não arma o botão. */
  searchAreaPending?: boolean;
  /** A lista em cena está sendo TROCADA por outra (#3984, a resposta do
   *  "buscar nesta área"): a coluna mostra só o esqueleto, e o mapa, os cortes e
   *  a ordem seguem em cena. A espera vai para a linha da meta pelo `notice` de
   *  tom `loading`, como a coleta inicial. */
  refreshing?: boolean;
  /** O pé da lista que oferece a busca OFFLINE pela equipe VOLL
   *  (`ResultsOfflineRequest`): copy, perfil e o envio são do host. Ausente ⇒
   *  a lista termina na sentinela, como antes. Não monta enquanto a lista
   *  completa carrega — oferecer a saída antes de a busca terminar é desistir
   *  por ela. */
  offlineRequest?: ResultsOfflineRequestProps;
  /**
   * A busca INICIAL ainda não respondeu (A6, QA prod 29/09: 17–45 s de tela
   * branca). A página já monta a forma dos resultados — título, filtros e
   * ordenador travados, a lista em esqueleto — e `status` vai para a linha da
   * meta, a região viva, com a mesma espera de "Carregando todos os hotéis". O
   * mapa entra como esqueleto: sem hotel não há pino a mostrar, e montá-lo agora
   * para remontar na chegada da lista seria uma segunda carga do mapa.
   */
  searching?: { readonly status: string };
  /** Quem rola a lista na linha (`ResultsLayout`): o host (o padrão) ou a
   *  própria página, quando o host lhe dá altura travada (`own`, #4587). */
  listScroll?: ResultsLayoutProps['listScroll'];
};

/**
 * A cadeia de derivação das ofertas da página: o corte e a ordem primeiro, a
 * projeção para a VIEW depois. As duas memoizadas.
 *
 * `mapOffers` memoizada, e não `filteredOffers.map(...)` no JSX — o mesmo
 * cuidado que o painel lateral antigo já tomava: array novo a cada render faz o
 * `HotelMap` recalcular a lista dele e o efeito dos marcadores remover e
 * recriar TODOS eles. Uma memo serve os dois consumidores — o mapa e a lista.
 */
function useHotelPageOffers(
  offers: readonly HotelFilterableOffer[],
  filters: HotelResultsFilterValue,
  order: HotelResultsOrder,
): {
  filteredOffers: readonly HotelFilterableOffer[];
  mapOffers: readonly HotelOfferView[];
} {
  const filteredOffers = useMemo(
    () =>
      sortHotelOffers(
        offers.filter((facts) => matchesHotelResultsFilters(facts, filters)),
        order,
      ),
    [filters, offers, order],
  );
  const mapOffers = useMemo(() => filteredOffers.map(({ offer }) => offer), [filteredOffers]);
  return { filteredOffers, mapOffers };
}

/** Limpa o ESTADO do pino quando o hotel sai da lista — mesma razão do painel
 *  da lista completa: sem isto, o pino voltava aceso quando o hotel
 *  reaparecesse (um filtro removido e reposto, por exemplo). */
function useActiveHotelCleanup(
  filteredOffers: readonly HotelFilterableOffer[],
  setActiveHotelId: Dispatch<SetStateAction<string | undefined>>,
): void {
  useEffect(() => {
    setActiveHotelId((current) =>
      current !== undefined && !filteredOffers.some(({ offer }) => offer.id === current)
        ? undefined
        : current,
    );
  }, [filteredOffers, setActiveHotelId]);
}

/** O mapa de hotéis no aside do layout. Recebe as ofertas JÁ projetadas para a
 *  view (ver a memo `mapOffers`): receber a lista crua aqui obrigaria a
 *  projetar no JSX, que é o defeito.
 *
 *  Duas peles, pelo `frame` do layout: ao LADO da lista é o cartão arredondado
 *  com o expandir para o mapa cheio; no PALCO map-centric (#3106) ele toma o
 *  palco inteiro sem moldura, enquadra os pinos acima da folha
 *  (`overlays.sheet`), centra os controles de vista no mapa que sobra
 *  (`--hui-map-visible`, publicada pelo palco) e abre a prévia do pino no pé
 *  (`dockedPreview`, #3108) — e não há para onde expandir: o mapa já é a
 *  tela. Com o card no pé, os controles centram na faixa que SOBRA acima dele
 *  (`--hui-map-preview-block`, publicada pelo mapa), com um piso para não
 *  subirem até a barra de busca (#3845). */
function HotelPageMap({
  frame,
  map,
  offers,
  resultsCopy,
  previewCloseLabel,
  searchArea,
  selectedHotelId,
  activeHotelId,
  locked,
  onMapExpand,
  onOpenDetails,
}: {
  readonly frame: ResultsAsideFrame;
  readonly previewCloseLabel: string;
  /** O "buscar nesta área" (#3114), só no palco. */
  readonly searchArea?: {
    readonly onSearchArea: (area: MapSearchArea) => void | Promise<void>;
    readonly copy: MapSearchAreaCopy;
    readonly pending: boolean;
  };
  readonly map: HotelMapView;
  readonly offers: readonly HotelOfferView[];
  readonly resultsCopy: HotelResultsCopy;
  readonly selectedHotelId?: string;
  readonly activeHotelId?: string;
  readonly locked: boolean;
  readonly onMapExpand: () => void;
  readonly onOpenDetails: (hotelId: string) => void;
}): ReactNode {
  const onStage = frame.placement === 'stage';
  return (
    <div
      className={
        onStage
          ? 'absolute inset-0 [&>*]:absolute [&>*]:inset-0 [&>*]:min-h-0 [&>*]:rounded-none [&>*]:border-0 [&_.iuk-results-map-view-controls]:top-[max(7.5rem,calc((var(--hui-map-visible)-var(--hui-map-preview-block,0px))/2))]'
          : 'flex min-h-0 flex-1 overflow-hidden rounded-2xl [&>*]:h-full [&>*]:w-full'
      }
    >
      <HotelMap
        map={map}
        offers={offers}
        {...(onStage
          ? {
              overlays: { sheet: frame.sheetHeight },
              // O toque no pino abre o card no pé do mapa (#3108); a folha
              // recolhe ao espiar para o card caber acima dela.
              dockedPreview: {
                closeLabel: previewCloseLabel,
                onOpenChange: (hotelId: string | undefined) => {
                  if (hotelId) frame.collapseSheet();
                },
              },
              ...(searchArea
                ? {
                    onSearchArea: searchArea.onSearchArea,
                    searchAreaCopy: searchArea.copy,
                    searchAreaPending: searchArea.pending,
                  }
                : {}),
            }
          : {})}
        selectLabel={resultsCopy.selectLabel}
        photoCopy={{
          previousPhotoLabel: resultsCopy.previousPhotoLabel,
          nextPhotoLabel: resultsCopy.nextPhotoLabel,
        }}
        detailsLabel={resultsCopy.detailsLabel}
        {...(selectedHotelId ? { selectedHotelId } : {})}
        {...(activeHotelId ? { activeHotelId } : {})}
        selectionLocked={locked}
        controls={{
          zoomInLabel: resultsCopy.mapZoomInLabel,
          zoomOutLabel: resultsCopy.mapZoomOutLabel,
          recenterLabel: resultsCopy.mapRecenterLabel,
          ...(onStage ? {} : { expandLabel: resultsCopy.mapExpandLabel, onExpand: onMapExpand }),
        }}
        onHotelSelect={onOpenDetails}
        onDetailsOpen={onOpenDetails}
      />
    </div>
  );
}

/** A linha da meta: a contagem ("8 de 214 hotéis") seguida do contexto da
 *  busca. Enquanto a lista completa carrega, a linha diz isso no lugar da
 *  contagem — o `aria-live` do layout anuncia a carga e, depois, o número. */
function metaLabel(
  copy: HotelResultsPageCopy,
  visible: number,
  total: number,
  notice: HotelResultsNotice | undefined,
): string {
  if (notice?.tone === 'loading') return notice.title;
  const count =
    visible === total ? copy.totalHotelsLabel(total) : copy.visibleHotelsLabel(visible, total);
  return copy.context ? `${count} · ${copy.context}` : count;
}

/** A folha de cortes do celular (#2663) que o layout monta quando a coluna sai
 *  da linha — o produto por dentro, o slot (aberto/fechar/foco) do layout. */
function filtersSheetRenderer(
  deps: Omit<
    Parameters<typeof HotelResultsFiltersSheet>[0],
    'open' | 'onOpenChange' | 'returnFocusTo'
  >,
): NonNullable<ResultsLayoutProps['filtersSheet']> {
  return (slot) => <HotelResultsFiltersSheet {...slot} {...deps} />;
}

/** O "buscar nesta área" do mapa do palco (#3114) — só com quem busca E a copy. */
function searchAreaProp(
  onSearchArea: HotelResultsPageProps['onSearchArea'],
  copy: MapSearchAreaCopy | undefined,
  pending: boolean,
): { searchArea?: NonNullable<Parameters<typeof HotelPageMap>[0]['searchArea']> } {
  if (!onSearchArea || !copy) return {};
  return { searchArea: { onSearchArea, copy, pending } };
}

/** A fila de chips recolhe enquanto a lista da folha cheia rola para baixo e
 *  volta quando ela rola para cima (#3274): o palco publica a direção, a
 *  página a traduz em `collapsed`. Sem slot não há fila, e nada é publicado. */
function useChipsCollapsed(hasSlot: boolean): {
  collapsed: boolean;
  onContentScrollDirection: ResultsLayoutProps['onContentScrollDirection'];
} {
  const [collapsed, setCollapsed] = useState(false);
  return {
    collapsed: hasSlot && collapsed,
    onContentScrollDirection: hasSlot
      ? (direction) => {
          setCollapsed(direction === 'down');
        }
      : undefined,
  };
}

/** A fila de chips no slot do host (#3140) — só com o slot aberto; com a
 *  busca inicial em voo, a silhueta dela (#5507). */
function chipsPortal(
  slot: Element | null,
  searching: boolean,
  props: Parameters<typeof HotelResultsFilterChips>[0],
): ReactNode {
  if (!slot) return null;
  const chips = searching ? <HotelFilterChipsSkeleton /> : <HotelResultsFilterChips {...props} />;
  return createPortal(chips, slot);
}

/** A busca inicial em voo é a página TRAVADA com o aviso de carga — a mesma
 *  espera de "Carregando todos os hotéis", dita pelo status do host. */
function withSearching(props: Readonly<HotelResultsPageProps>): Readonly<HotelResultsPageProps> {
  if (!props.searching) return props;
  return {
    ...props,
    locked: true,
    detailsLocked: true,
    notice: { tone: 'loading', title: props.searching.status },
  };
}

/** O lugar do mapa enquanto a busca inicial corre: a mesma moldura do mapa (ao
 *  lado, cartão arredondado; no palco estreito, a tela inteira), decorativa. */
function HotelPageMapSkeleton({ frame }: { readonly frame: ResultsAsideFrame }): ReactNode {
  const still = useSkeletonMotion();
  return (
    <div
      aria-hidden="true"
      className={
        frame.placement === 'stage'
          ? 'absolute inset-0'
          : 'flex min-h-0 flex-1 overflow-hidden rounded-2xl'
      }
    >
      <Skeleton className={`size-full rounded-none ${still}`} />
    </div>
  );
}

/** O aside do layout: o mapa de hotéis e o botão que o abre em tela cheia — ou,
 *  com a busca inicial em voo, só o esqueleto dele (sem mapa não há o que abrir). */
function asideProps(
  page: Readonly<HotelResultsPageProps>,
  offers: readonly HotelOfferView[],
  activeHotelId: string | undefined,
): Pick<ResultsLayoutProps, 'aside' | 'onAsideOpen'> {
  if (page.searching) return { aside: (frame) => <HotelPageMapSkeleton frame={frame} /> };
  const { copy, selectedHotelId } = page;
  return {
    aside: (frame) => (
      <HotelPageMap
        frame={frame}
        map={page.map}
        offers={offers}
        resultsCopy={page.resultsCopy}
        previewCloseLabel={copy.mapPreviewCloseLabel}
        {...searchAreaProp(page.onSearchArea, copy.searchArea, page.searchAreaPending ?? false)}
        {...(selectedHotelId ? { selectedHotelId } : {})}
        {...(activeHotelId ? { activeHotelId } : {})}
        locked={page.locked ?? false}
        onMapExpand={page.onMapExpand}
        onOpenDetails={page.onOpenDetails}
      />
    ),
    onAsideOpen: page.onMapExpand,
  };
}

/**
 * A página de resultados da busca DETERMINÍSTICA de hotel — o
 * `ResultadoDoHotel` do protótipo: o layout compartilhado de resultados
 * (`ResultsLayout`, #2994 — título + compartilhar, meta + ordenador, coluna de
 * filtros, lista, mapa ao lado) preenchido com as peças da lista completa: o
 * painel de cortes do hotel (e a folha dele no celular), o ordenador, os cards
 * empilhados e o mapa de hotéis.
 */
/** O corpo da coluna: a lista ou, quando o corte esconde TODOS os hotéis, o
 *  aviso com "Limpar filtros" (#3217 C4). O cartão de aviso/erro da busca
 *  (`ResultsNotice`) fica nos DOIS ramos — é a lista que o carrega, e o corte
 *  vazio não pode esconder "alguns fornecedores falharam". Recebe as props da
 *  página inteiras (é uma fatia dela, não um componente com contrato próprio)
 *  mais o que só o host calcula: as ofertas cortadas, a trava do detalhe e o
 *  foco. Fora do host pelo teto de linhas/complexidade de `HotelResultsPage`. */
function HotelPageListBody({
  page,
  list,
  filteredEmpty,
  onClearFilters,
}: {
  readonly page: Readonly<HotelResultsPageProps>;
  readonly list: {
    readonly offers: readonly HotelOfferView[];
    readonly busy: boolean;
    readonly onOfferFocus: (hotelId?: string) => void;
  };
  readonly filteredEmpty: boolean;
  readonly onClearFilters: () => void;
}): ReactNode {
  const notice = decisionNotice(page.notice);
  const refreshing = page.refreshing === true;
  const pending = refreshing || page.notice?.tone === 'loading';
  // O pé da lista nos DOIS ramos: o corte que esconde todos os hotéis é
  // exatamente o momento em que a saída offline serve.
  const offlineFoot =
    page.offlineRequest && !pending ? (
      <div className="mt-4">
        <ResultsOfflineRequest {...page.offlineRequest} />
      </div>
    ) : null;
  if (filteredEmpty && !refreshing) {
    return (
      <>
        {notice && <ResultsNotice notice={notice} />}
        <ResultsFilteredEmpty
          copy={page.copy.filteredEmpty}
          onClear={page.locked ? undefined : onClearFilters}
        />
        {offlineFoot}
      </>
    );
  }
  return (
    <>
      <HotelResultsListArea
        offers={refreshing ? [] : list.offers}
        copy={page.resultsCopy}
        selectedHotelId={page.selectedHotelId}
        busy={list.busy}
        notice={notice}
        pending={pending}
        hasMore={page.hasMore === true}
        loadingMore={page.loadingMore === true}
        loadMoreLabel={page.loadMoreLabel}
        onOfferFocus={list.onOfferFocus}
        onLoadMore={page.onLoadMore}
        onOpenDetails={page.onOpenDetails}
      />
      {offlineFoot}
    </>
  );
}

export function HotelResultsPage(input: Readonly<HotelResultsPageProps>): ReactNode {
  const props = withSearching(input);
  const {
    offers,
    currency,
    filtersCopy,
    copy,
    filters,
    order,
    locked = false,
    total,
    notice,
    onQueryChange,
    detailsLocked = locked,
    onShare,
    pdfShare,
    chipsContainer,
  } = props;
  const [activeHotelId, setActiveHotelId] = useState<string>();
  /* O palco ESTREITO é decidido pela MESMA régua da barra e do palco de
     resultado (`HOTEL_NARROW_QUERY`), não pela régua de espaço da linha: a
     linha diz se as colunas cabem; a largura da tela diz se o mapa é o palco e
     a lista uma folha por cima dele (`narrowAside="stage"`, #3106). */
  const narrow = useMediaQuery(HOTEL_NARROW_QUERY);
  const applyFilters = (next: HotelResultsFilterValue) => {
    onQueryChange({ filters: next, order });
  };
  const { filteredOffers, mapOffers } = useHotelPageOffers(offers, filters, order);
  useActiveHotelCleanup(filteredOffers, setActiveHotelId);

  /* O fornecedor tem mais hotéis do que os carregados (o "N de M" da meta):
     as contagens dos filtros são da amostra, e os eixos de corte no servidor
     ficam clicáveis em zero (`countsPartial` — achado #02 da auditoria 30/08). */
  const countsPartial = total !== undefined && total > offers.length;
  // Os chips só existem no estreito e com o slot do host aberto.
  const chipsSlot = narrow && chipsContainer ? chipsContainer : null;
  const chipsCollapse = useChipsCollapsed(chipsSlot !== null);
  // Só a busca INICIAL (vazia) vira esqueleto; a carga incremental, com
  // ofertas na tela, mantém os cortes reais (#5507).
  const searching = input.searching !== undefined;
  const renderFiltersSheet = filtersSheetRenderer({
    offers,
    currency,
    value: filters,
    copy: filtersCopy,
    locked,
    countsPartial,
    searching,
    onApply: applyFilters,
  });
  return (
    <>
      {chipsPortal(chipsSlot, searching, {
        offers,
        currency,
        filters,
        order,
        filtersCopy,
        sortCopy: copy.sort,
        copy: copy.chips,
        locked,
        countsPartial,
        collapsed: chipsCollapse.collapsed,
        onQueryChange,
      })}
      <ResultsLayout
        copy={{
          filtersLabel: filtersCopy.filtersLabel,
          resetFiltersLabel: filtersCopy.resetFiltersLabel,
          asideLabel: copy.showMapLabel,
        }}
        metaControls={chipsSlot ? 'none' : 'inline'}
        {...(chipsCollapse.onContentScrollDirection
          ? { onContentScrollDirection: chipsCollapse.onContentScrollDirection }
          : {})}
        narrow={narrow}
        narrowAside="stage"
        listScroll={props.listScroll}
        title={copy.title}
        {...titleShareOf(resultsShareOf(copy.shareLabel, onShare, pdfShare, mapOffers))}
        meta={metaLabel(copy, filteredOffers.length, total ?? offers.length, notice)}
        metaActions={
          <HotelResultsSort
            value={order}
            copy={copy.sort}
            disabled={locked}
            onChange={(next) => {
              onQueryChange({ filters, order: next });
            }}
          />
        }
        activeFilterCount={activeFilterCount(filters)}
        onResetFilters={() => {
          applyFilters(EMPTY_HOTEL_RESULTS_FILTERS);
        }}
        filters={
          searching ? (
            <HotelResultsFiltersSkeleton copy={filtersCopy} />
          ) : (
            <HotelResultsFilters
              offers={offers}
              currency={currency}
              value={filters}
              copy={filtersCopy}
              locked={locked}
              countsPartial={countsPartial}
              frame={false}
              header={false}
              onChange={applyFilters}
            />
          )
        }
        filtersSheet={renderFiltersSheet}
        content={
          <div className="flex min-h-0 flex-1 flex-col">
            <HotelPageListBody
              page={props}
              list={{ offers: mapOffers, busy: detailsLocked, onOfferFocus: setActiveHotelId }}
              filteredEmpty={
                offers.length > 0 && filteredOffers.length === 0 && notice?.tone !== 'loading'
              }
              onClearFilters={() => {
                applyFilters(EMPTY_HOTEL_RESULTS_FILTERS);
              }}
            />
          </div>
        }
        {...asideProps(props, mapOffers, activeHotelId)}
      />
    </>
  );
}
